import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import PageSolutionCategoriesDto, {
	PageSolutionCategoriesTableCreatePayloadDto,
	PageSolutionCategoriesTableQueryParamsDto,
	PageSolutionCategoriesTableUpdatePayloadDto,
} from "dto/pages/solution-categories/page-solution-categories-dto";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import SolutionCategoriesTable from "components/pages/solution-categories/app-solution-categories-table";

const PageSolutionCategories = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const [isFetching, setIsFetching] = useState(false);
	const [disableAddNew, setDisableAddNew] = useState(false);
	const [newRow, setNewRow] = useState<PageSolutionCategoriesTableCreatePayloadDto>();
	const [partnerData, setPartnerData] = useState<PageSolutionCategoriesDto>();
	const queryParams = useRef(getQueryParams<PageSolutionCategoriesTableQueryParamsDto>(searchParams, { page: 0 }));

	const onHandleGetPartners = useCallback(async () => {
		setIsFetching(true);

		try {
			const { page } = queryParams.current;
			const payload = { size: 10, page };
			const response = await api.get.solution_categories.categories(payload);

			setPartnerData(response.data.data.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, []);

	//prettier-ignore
	const onHandleAddPartner = useCallback(async (payload: PageSolutionCategoriesTableCreatePayloadDto) => {
		let response = null;

		try {
			await api.post.solution_categories.create(payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);

			if (partnerData && partnerData?.numberOfElements === partnerData.size) {
				queryParams.current.page = partnerData.totalElements % partnerData.size === 0 ? partnerData.totalPages : partnerData.totalPages - 1;
			}

			onHandleGetPartners();
		}
	}, [partnerData, onHandleGetPartners]);

	//prettier-ignore
	const onUpdatePartners = useCallback(async (payload: PageSolutionCategoriesTableUpdatePayloadDto, page? : number) => {
		let response = null;
		
		try {
			await api.put.solution_categories.update({ id: payload.id }, payload);
			
			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);

			if (typeof page === "number") {
				queryParams.current.page = page;
			}

			onHandleGetPartners();
		}
	}, [onHandleGetPartners]);

	const onHandlePageChange = (page: number) => {
		queryParams.current.page = page;

		memoSetSearchParams.current(encodeQueryParams(queryParams.current));

		onHandleGetPartners();
	};

	const onHandleAddNew = () => {
		setDisableAddNew(true);
		setNewRow({ title: "", description: "", fullDescription: "", category: "", status: "active" });
	};

	useEffect(() => {
		onHandleGetPartners();
	}, [onHandleGetPartners]);

	return (
		<div className="page-solution-categories">
			<div className="categories">
				<AppHeading title="Solution Categories">
					<AppHeading.Divider />
				</AppHeading>

				<div className="categories__button">
					<Button label="Add New" className="addNewBtn" disabled={disableAddNew} onClick={onHandleAddNew} />
				</div>

				{/* prettier-ignore */}
				<SolutionCategoriesTable data={partnerData?.content || []} setDisableAddNew={setDisableAddNew} newRow={newRow} setNewRow={setNewRow} onUpdatePartners={onUpdatePartners} onCreatePartner={onHandleAddPartner} onHandlePageChange={onHandlePageChange} fetching={isFetching} paginationTable={{currentPage: partnerData?.number || 0, totalPages: partnerData?.totalPages || 0, totalElements: partnerData?.totalElements || 0, numberOfElements: partnerData?.numberOfElements || 0, tableSize: partnerData?.size || 10 }} />
			</div>
		</div>
	);
};

export default PageSolutionCategories;
